import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Steps, Tabs } from 'antd';
import { FormikProps } from 'formik';
import { isNil } from 'lodash';
import cx from 'classnames';

import {
  BASE_ALERTS_CONTAINER_ID,
  DEFAULT_TOAST_DURATION,
} from 'utils/toastUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  getAvailableRolesForRegistration,
  getUserDetails,
  logout,
  saveProfileSetup,
} from 'redux/reducers/authenticationSlice';
import {
  mapOutreachDataToRequest,
  OutreachFormValues,
} from './RepresentativeOutreach/mapUtils';
import useFormSteps, { FormStep } from 'hooks/useFormSteps';
import {
  BOUserTypeEnum,
  InitialProfileRequestModel,
  ProfileTypeEnum,
} from 'api/backofficeApiClient';
import RepresentativeOutreach, {
  MemberContacts,
} from './RepresentativeOutreach';
import { StepValidationStatuses } from 'enums';
import EntitySelection, {
  EntitySelectionFormValues,
  Profile,
} from './EntitySelection';
import {
  CompanyProfileFormValues,
  mapCompanyProfileToRequest,
} from 'components/modals/ProfileModals/CompanyProfileModal/CompanyProfileForm/mapUtils';
import CompanyProfileForm from 'components/modals/ProfileModals/CompanyProfileModal/CompanyProfileForm';
import {
  mapSelftEployedProfileToRequest,
  SelfEmployedProfileFormValues,
} from 'components/modals/ProfileModals/SelfEmployedProfileModal/SelfEmployedProfileForm/mapUtils';
import SelfEmployedProfileForm from 'components/modals/ProfileModals/SelfEmployedProfileModal/SelfEmployedProfileForm';
import {
  InternationalProfileFormValues,
  mapInternationProfileToRequest,
} from 'components/modals/ProfileModals/InternationalProfileModal/InternationalProfileForm/mapUtils';
import InternationalProfileForm from 'components/modals/ProfileModals/InternationalProfileModal/InternationalProfileForm';
import CongratulationsModal from 'components/modals/CongratulationsModal';
import BaseToast from 'components/wrappers/BaseToast';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './ProfileSetupForm.module.less';

type EntitySelectionFormikProps = FormikProps<EntitySelectionFormValues>;
type OutreachFormikProps = FormikProps<OutreachFormValues>;
type CompanyProfileFormikProps = FormikProps<CompanyProfileFormValues>;
type SelfEmployedProfileFormikProps =
  FormikProps<SelfEmployedProfileFormValues>;
type InternationalProfileFormikProps =
  FormikProps<InternationalProfileFormValues>;
type ProfileFormikProps =
  | CompanyProfileFormikProps
  | SelfEmployedProfileFormikProps
  | InternationalProfileFormikProps;

export enum StepKeys {
  EntitySelection,
  Profile,
  Outreach,
}

const { Step } = Steps;

export const DEFAULT_ACTIVE_STEP = StepKeys.EntitySelection;

const initialProfile = {
  entity: null,
  type: null,
};

const initialMemberContacts: MemberContacts = {
  email: '',
  phone: '',
};

const SetupDoneModal = (
  <CongratulationsModal
    content={
      <div className={styles.modalText}>
        You are almost done with the setup. <br />
        Now let us show you around.
      </div>
    }
  />
);

type Props = {
  onStepChange: React.Dispatch<React.SetStateAction<number>>;
};

export default function ProfileSetupForm(props: Props) {
  const { onStepChange } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState<Profile>(initialProfile);
  const [isLoading, setIsLoading] = useState(false);
  const [availableSelectionRoles, setAvailableSelectionRoles] = useState<
    ProfileTypeEnum[]
  >([]);
  const [memberContacts, setMemberContacts] = useState(initialMemberContacts);
  const isCorporateTASelected = profile.type === ProfileTypeEnum.Corporate;
  const isInternational = profile.entity === BOUserTypeEnum.International;

  const profileFormRef: any = useRef();
  const entitySelectionFormRef: any = useRef();
  const representativeOutreachFormRef: any = useRef();

  const profileFormSteps = useMemo(
    (): FormStep[] => [
      {
        status: StepValidationStatuses.Process,
        isValid: false,
      },
      {
        status: StepValidationStatuses.Wait,
        isValid: false,
      },
      {
        status: StepValidationStatuses.Wait,
        isValid: isCorporateTASelected,
      },
    ],
    [isCorporateTASelected],
  );

  const [formSteps, onFormChange, activeStep, onActiveStepChange, resetSteps] =
    useFormSteps(profileFormSteps, DEFAULT_ACTIVE_STEP);

  useEffect(() => {
    if (
      activeStep !== StepKeys.Outreach ||
      !profileFormRef?.current ||
      (!!memberContacts.phone && !!memberContacts.email)
    ) {
      return;
    }

    let contactEmail = '';

    if (
      isNil(profileFormRef.current.values.emailForCommunications?.sameAsOwner)
    ) {
      contactEmail = profileFormRef.current.values.ownerInfo.email;
    } else {
      contactEmail = profileFormRef.current.values.emailForCommunications
        .sameAsOwner
        ? profileFormRef.current.values.ownerInfo.email
        : profileFormRef.current.values.emailForCommunications.email;
    }

    setMemberContacts({
      email: contactEmail,
      phone: profileFormRef.current.values.businessPhone,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  useEffect(() => {
    loadAvailableRolesForRegistration();
    // eslint-disable-next-line
  }, []);

  const loadUserDetails = async () => {
    await dispatch(getUserDetails());
  };

  const loadAvailableRolesForRegistration = async () => {
    const result = await dispatch(getAvailableRolesForRegistration());

    if (getAvailableRolesForRegistration.fulfilled.match(result)) {
      setAvailableSelectionRoles(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const getCurrentFormRef = () => {
    switch (activeStep) {
      case StepKeys.EntitySelection:
        return entitySelectionFormRef.current;

      case StepKeys.Profile:
        return profileFormRef.current;

      case StepKeys.Outreach:
        return representativeOutreachFormRef.current;
    }
  };

  const validateCurrentForm = async () => {
    await getCurrentFormRef()?.submitForm();

    resetFormSubmitting();
  };

  const handlePrevStep = async () => {
    await validateCurrentForm();

    onActiveStepChange((step) => step - 1);
    onStepChange((step) => step - 1);
  };

  const handleNextStep = async () => {
    await validateCurrentForm();

    if (getCurrentFormRef()?.isValid) {
      onActiveStepChange((step) => step + 1);
      onStepChange((step) => step + 1);
    }
  };

  const isFormDirty = () => {
    return [
      profileFormRef.current?.dirty,
      entitySelectionFormRef.current?.dirty,
      representativeOutreachFormRef.current?.dirty,
    ].some(Boolean);
  };

  const isFormValid = () => {
    return [
      profileFormRef.current?.isValid,
      entitySelectionFormRef.current?.isValid,
      representativeOutreachFormRef.current?.isValid,
    ].every(Boolean);
  };

  const isFormSubmitting = () => {
    return [
      profileFormRef.current?.isSubmitting,
      entitySelectionFormRef.current?.isSubmitting,
      representativeOutreachFormRef.current?.isSubmitting,
    ].every(Boolean);
  };

  const resetForm = () => {
    [
      profileFormRef?.current,
      entitySelectionFormRef?.current,
      representativeOutreachFormRef?.current,
    ].forEach((form) => form?.resetForm());
  };

  const resetFormSubmitting = () => {
    [
      profileFormRef?.current,
      entitySelectionFormRef?.current,
      representativeOutreachFormRef?.current,
    ].forEach((form) => form?.setSubmitting(false));
  };

  const handleLogout = () => {
    resetForm();
    resetSteps();
    setMemberContacts(initialMemberContacts);
    setProfile(initialProfile);
    dispatch(logout());
    navigate('/login');
  };

  const handleSubmit = async () => {
    const entitySelectionForm: EntitySelectionFormikProps =
      entitySelectionFormRef.current;
    const profileForm: ProfileFormikProps = profileFormRef.current;
    const representativeOutreachForm: OutreachFormikProps =
      representativeOutreachFormRef.current;

    await Promise.all([
      entitySelectionForm.submitForm(),
      profileForm.submitForm(),
      representativeOutreachForm.submitForm(),
    ]);

    if (!isFormValid()) return;

    const data = getProfileRequestModel();

    setIsLoading(true);
    const result = await dispatch(saveProfileSetup(data));
    setIsLoading(false);

    if (saveProfileSetup.fulfilled.match(result)) {
      await loadUserDetails();
      BaseToast(SetupDoneModal, {
        containerId: BASE_ALERTS_CONTAINER_ID,
        autoClose: DEFAULT_TOAST_DURATION,
        className: styles.welcomeClose,
      });

      navigate('/show-around', { replace: true });
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
      resetFormSubmitting();
    }
  };

  const getProfileRequestModel = (): InitialProfileRequestModel => {
    const entitySelectionForm: EntitySelectionFormikProps =
      entitySelectionFormRef.current;
    const profileForm: ProfileFormikProps = profileFormRef.current;
    const representativeOutreachForm: OutreachFormikProps =
      representativeOutreachFormRef.current;

    const { entity, profileType } = entitySelectionForm.values;

    const companyProfileData =
      entity === BOUserTypeEnum.Company
        ? mapCompanyProfileToRequest(
            profileForm.values as CompanyProfileFormValues,
            { profileType: profileType as ProfileTypeEnum },
          )
        : undefined;
    const selfEmployedProfileData =
      entity === BOUserTypeEnum.SelfEmployed
        ? mapSelftEployedProfileToRequest(
            profileForm.values as SelfEmployedProfileFormValues,
          )
        : undefined;
    const internationalProfileData =
      entity === BOUserTypeEnum.International
        ? mapInternationProfileToRequest(
            profileForm.values as InternationalProfileFormValues,
            { profileType: profileType as ProfileTypeEnum },
          )
        : undefined;
    const outreach = mapOutreachDataToRequest(
      representativeOutreachForm.values,
    );

    return {
      userType: entity as BOUserTypeEnum,
      profileType: !isNil(profileType) ? profileType : undefined,
      outreach,
      company: companyProfileData,
      selfEmployed: selfEmployedProfileData,
      international: internationalProfileData,
    };
  };

  const handleModalCancel = () => {
    if (isFormSubmitting()) return;

    let title = 'Are you sure you want to log out? You can always log back.';
    if (isFormDirty()) {
      title = "Are you sure you want to log out? Entered data won't be saved.";
    }

    Modal.confirm({
      title,
      okText: 'Log out',
      icon: <span className="icon icon-attention-filled" />,
      onCancel() {},
      onOk() {
        handleLogout();
      },
    });
  };

  const getProfileFormByEntity = () => {
    if (!entitySelectionFormRef.current) return null;

    const profileFormProps = {
      formRef: profileFormRef,
      isActive: activeStep === StepKeys.Profile,
      onChange: onFormChange[StepKeys.Profile],
      profileType: entitySelectionFormRef.current.values.profileType,
    };

    switch (entitySelectionFormRef.current.values.entity) {
      case BOUserTypeEnum.Company:
        return <CompanyProfileForm {...profileFormProps} />;

      case BOUserTypeEnum.SelfEmployed:
        return <SelfEmployedProfileForm {...profileFormProps} />;

      case BOUserTypeEnum.International:
        return <InternationalProfileForm {...profileFormProps} />;

      default:
        return null;
    }
  };

  const reset = () => {
    resetSteps();
    setMemberContacts(initialMemberContacts);
    profileFormRef?.current?.resetForm();
    representativeOutreachFormRef?.current?.resetForm();
  };

  const getStepClassNames = (stepKey: StepKeys) => {
    const { status } = formSteps[stepKey];

    return {
      active: activeStep === stepKey,
      success:
        status === StepValidationStatuses.Process ||
        status === StepValidationStatuses.Finish,
      error: status === StepValidationStatuses.Error,
    };
  };

  return (
    <>
      <section className={cx(styles.initialProfileSection)}>
        <div className={styles.stepTitle}>Let's get to know you</div>

        <Steps direction="horizontal" className="custom-steps">
          <Step
            icon={<span className="icon icon-biography no-actions" />}
            status={formSteps[StepKeys.EntitySelection].status}
            className={cx(getStepClassNames(StepKeys.EntitySelection))}
          />
          <Step
            icon={<span className="icon icon-portfolio no-actions" />}
            status={formSteps[StepKeys.Profile].status}
            className={cx(getStepClassNames(StepKeys.Profile))}
          />
          <Step
            icon={<span className="icon icon-watch no-actions" />}
            status={formSteps[StepKeys.Outreach].status}
            className={cx(getStepClassNames(StepKeys.Outreach))}
          />
        </Steps>

        <Tabs
          animated={false}
          activeKey={`${activeStep}`}
          className={styles.tab}
        >
          <Tabs.TabPane tab={null} key={`${StepKeys.EntitySelection}`}>
            <EntitySelection
              onReset={reset}
              profile={profile}
              onProfileChange={setProfile}
              formRef={entitySelectionFormRef}
              availableSelectionRoles={availableSelectionRoles}
              onChange={onFormChange[StepKeys.EntitySelection]}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab={null} key={`${StepKeys.Profile}`}>
            {getProfileFormByEntity()}
          </Tabs.TabPane>

          <Tabs.TabPane tab={null} key={`${StepKeys.Outreach}`}>
            <RepresentativeOutreach
              memberContacts={memberContacts}
              isInternational={isInternational}
              isRequired={!isCorporateTASelected}
              formRef={representativeOutreachFormRef}
              onChange={onFormChange[StepKeys.Outreach]}
              isActive={activeStep === StepKeys.Outreach}
            />
          </Tabs.TabPane>
        </Tabs>

        <div className={styles.stepFooter}>
          {activeStep !== StepKeys.EntitySelection && (
            <Button
              className="with-icon modal-icon-btn"
              onClick={handlePrevStep}
            >
              <span className="icon icon-double-left" />
              Back
            </Button>
          )}

          {activeStep < formSteps.length - 1 && (
            <Button type="primary" onClick={handleNextStep}>
              Next
            </Button>
          )}

          {activeStep === formSteps.length - 1 && (
            <Button
              type="primary"
              disabled={isLoading}
              loading={isLoading}
              onClick={handleSubmit}
              data-id="submitInitialProfileSetup"
            >
              Save
            </Button>
          )}
        </div>
      </section>

      <div className={styles.closeBtn} onClick={handleModalCancel}>
        <span className="icon icon-close-sm"></span>
      </div>
    </>
  );
}
